<script setup lang="ts">
import { ref, computed } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import type { Schemas } from '#shopware'

interface CmsElementSimilarProducts {
    id: string
    type: 'similar-products'
    slot: string
    versionId?: string
    config: Record<string, never>
    data?: {
        product?: Schemas['Product']
    }
}

const props = defineProps<{
    content: CmsElementSimilarProducts
}>()

const { product } = useProduct(
    props.content?.data?.product ?? undefined
)

const productId = computed(() => product.value?.id ?? null)

const { apiClient } = useShopwareContext()

const similarProducts = ref<Schemas['Product'][]>([])
const isLoading = ref(true)
const elementRef = ref<HTMLElement | null>(null)

const loadSimilarProducts = async (id: string) => {
    try {
        const result = await apiClient.invoke('readSimilarProducts POST /similar-products/{productId}', {
            pathParams: { productId: id },
            body: {
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated',
                        'parentId',
                        'productNumber',
                        'markAsTopseller',
                        'customFields',
                        'calculatedPrice',
                        'calculatedPrices',
                        'calculatedCheapestPrice',
                        'cover',
                        'coverId',
                        'manufacturer',
                        'manufacturerId',
                        'seoUrls',
                    ],
                    product_media: ['media', 'mediaId'],
                    media: ['url', 'alt', 'title'],
                    product_manufacturer: ['id', 'name', 'translated'],
                    seo_url: ['seoPathInfo', 'pathInfo'],
                },
                associations: {
                    cover: {
                        associations: {media: {}}
                    },
                    manufacturer: {},
                    seoUrls: {},
                }
            }
        })


        similarProducts.value = result?.data?.elements ?? []
    } catch {
        similarProducts.value = []
    } finally {
        isLoading.value = false
    }
}

const { stop } = useIntersectionObserver(
    elementRef,
    (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && isLoading.value && productId.value) {
            loadSimilarProducts(productId.value)
            stop()
        }
    },
    {
        threshold: 0.1,
        rootMargin: '200px'
    }
)

const shouldRender = computed(() => {
    return productId.value && similarProducts.value.length > 0
})
</script>

<template>
    <div
        v-if="productId"
        ref="elementRef"
        class="cms-element-similar-products"
    >
        <ComponentProductCarousel
            v-if="shouldRender"
            :id="`similar-products-${content.id}`"
            :products="similarProducts"
            :title="$t('product.similarProducts')"
            :show-title="true"
            :loading="isLoading"
            layout-type="minimal"
            :items-per-slide="{
                default: 1,
                md: 2,
                lg: 3,
                xl: 4
            }"
            :gap="10"
            show-navigation="outside"
            :show-indicators="false"
            :auto-play="false"
            :loop="false"
            :show-wishlist="true"
            :show-badges="true"
            :hide-listing-parent-price="true"
        />
    </div>
</template>
